import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../../../lib/hooks';
import { PageHeader } from '../../../components/layout/PageHeader';
import { PageSection } from '../../../components/layout/PageSection';
import { StatCard } from '../../../components/layout/StatCard';
import { Button } from '../../../components/ui/Button';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Money } from '../../../components/ui/Money';
import { vaultHooks, type PendingHandover } from '../../../features/accounting/network/vault.api';
import { AcceptHandoverModal } from '../../../features/accounting/components/RecordHandoverModal';
import { RejectHandoverModal } from '../../../features/accounting/components/RejectHandoverModal';

export const Route = createFileRoute('/_auth/finance/handovers')({
  component: HandoverQueuePage,
});

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'warning',
  accepted: 'success',
  rejected: 'danger',
};

function HandoverQueuePage() {
  usePageTitle('Cash Handovers');
  const [statusFilter, setStatusFilter] = React.useState<'pending' | 'accepted' | 'rejected'>('pending');
  const { data, isLoading, refetch, isFetching } = vaultHooks.useHandovers(statusFilter);
  const { data: positionsData } = vaultHooks.useAgentCashPositions(true);
  const [acceptOpen, setAcceptOpen] = React.useState(false);
  const [rejectTarget, setRejectTarget] = React.useState<PendingHandover | null>(null);

  const handovers = Array.isArray(data) ? data : (data?.data ?? []);
  const totalHeldPaise = positionsData?.totalHeldPaise ?? 0;
  const pendingCount = statusFilter === 'pending' ? handovers.length : handovers.filter((h) => h.status === 'pending').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agent Cash Handovers"
        subtitle="Accept or reject cash handovers submitted by field agents. Acceptance moves the counted amount into the branch vault; rejection returns the staged receipts to the agent."
        action={
          <Button variant="primary" onClick={() => setAcceptOpen(true)}>
            Accept Handover
          </Button>
        }
      />

      <PageSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            label="Outstanding Agent Custody (branch)"
            value={<Money paise={totalHeldPaise} />}
          />
          <StatCard label="Pending Handovers" value={String(pendingCount)} />
          <StatCard label="Queue Filter" value={statusFilter} />
        </div>
      </PageSection>

      <PageSection>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-text-primary">Handover Queue</h3>
          <div className="flex gap-2">
            {(['pending', 'accepted', 'rejected'] as const).map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? 'primary' : 'secondary'}
                onClick={() => setStatusFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
            <Button variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
              Refresh
            </Button>
          </div>
        </div>

        {isLoading ? (
          <p className="text-text-muted text-sm py-8 text-center">Loading handovers…</p>
        ) : handovers.length === 0 ? (
          <p className="text-text-muted text-sm py-8 text-center">
            No {statusFilter} handovers at this branch.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-text-secondary border-b border-border">
                  <th className="py-2 pr-4 font-medium">Submitted</th>
                  <th className="py-2 pr-4 font-medium">Agent</th>
                  <th className="py-2 pr-4 font-medium text-right">Expected</th>
                  <th className="py-2 pr-4 font-medium text-right">Counted</th>
                  <th className="py-2 pr-4 font-medium text-right">Discrepancy</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Detail</th>
                  <th className="py-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {handovers.map((h) => (
                  <tr key={h.id} className="border-b border-border last:border-0">
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {new Date(h.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </td>
                    <td className="py-3 pr-4">{h.agentName ?? h.agentId}</td>
                    <td className="py-3 pr-4 text-right tabular-nums">
                      <Money paise={h.expectedAmountPaise ?? 0} />
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">
                      {h.amountPaise != null && h.status !== 'pending' ? <Money paise={h.amountPaise} /> : '—'}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">
                      {h.discrepancyPaise ? (
                        <span className={h.discrepancyPaise < 0 ? 'text-red-600' : 'text-green-700'}>
                          <Money paise={Math.abs(h.discrepancyPaise)} />
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-3 pr-4">
                      <StatusBadge status={STATUS_VARIANTS[h.status] ?? 'info'} label={h.status} />
                    </td>
                    <td className="py-3 pr-4 max-w-xs truncate text-text-secondary" title={h.rejectionReason ?? h.notes ?? undefined}>
                      {h.rejectionReason ?? h.notes ?? '—'}
                    </td>
                    <td className="py-3 text-right">
                      {h.status === 'pending' ? (
                        <Button variant="danger" onClick={() => setRejectTarget(h)}>
                          Reject
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>

      <AcceptHandoverModal open={acceptOpen} onClose={() => setAcceptOpen(false)} />
      <RejectHandoverModal
        handover={rejectTarget}
        onClose={() => setRejectTarget(null)}
      />
    </div>
  );
}
